import React from "react";
import { Box, Typography, Chip, Button, Stack } from "@mui/material";
import ErrorOutlineIcon from "@mui/icons-material/ErrorOutline";
import {
  humanizeKey,
  formatCellValue,
  getDataDisplayMode,
  MAX_TABLE_ROWS,
  CHATBOT_TEAL,
  formatMessageTimestamp,
} from "./chatbotFormat";

const bubbleBase = {
  maxWidth: "85%",
  borderRadius: "14px",
  padding: "10px 14px",
  fontSize: "14px",
  lineHeight: 1.5,
  wordBreak: "break-word",
};

const ParamsRow = ({ params }) => {
  const entries = Object.entries(params || {}).filter(
    ([, v]) => v !== null && v !== undefined && v !== ""
  );
  if (entries.length === 0) return null;

  return (
    <Stack direction="row" flexWrap="wrap" gap={0.75} sx={{ mt: 1 }}>
      {entries.map(([key, value]) => (
        <Chip
          key={key}
          size="small"
          label={`${humanizeKey(key)}: ${value}`}
          sx={{
            backgroundColor: "var(--chip-bg-color)",
            color: "var(--chip-text-color)",
            border: "1px solid var(--chip-border-color)",
            fontSize: "11px",
            height: "22px",
          }}
        />
      ))}
    </Stack>
  );
};

const DataChips = ({ data }) => {
  const field = Object.keys(data[0])[0];
  return (
    <Stack direction="row" flexWrap="wrap" gap={0.75} sx={{ mt: 1.25 }}>
      {data.map((row, i) => (
        <Chip
          key={i}
          size="small"
          label={formatCellValue(row[field])}
          sx={{
            backgroundColor: "var(--cell-bg-color)",
            color: "var(--text-color)",
            border: "1px solid var(--border-color)",
          }}
        />
      ))}
    </Stack>
  );
};

const DataKeyValue = ({ data }) => {
  const row = data[0];
  return (
    <Box
      sx={{
        mt: 1.25,
        border: "1px solid var(--border-color)",
        borderRadius: "10px",
        overflow: "hidden",
      }}
    >
      {Object.entries(row).map(([key, value], i) => (
        <Box
          key={key}
          sx={{
            display: "flex",
            justifyContent: "space-between",
            gap: 2,
            padding: "6px 10px",
            backgroundColor: i % 2 === 0 ? "var(--cell-bg-color)" : "var(--header-bg-color)",
            fontSize: "13px",
          }}
        >
          <Typography component="span" sx={{ color: "var(--placeholder-color)", fontSize: "inherit" }}>
            {humanizeKey(key)}
          </Typography>
          <Typography component="span" sx={{ color: "var(--text-color)", fontSize: "inherit", fontWeight: 600 }}>
            {formatCellValue(value)}
          </Typography>
        </Box>
      ))}
    </Box>
  );
};

const DataTable = ({ data, rowCount }) => {
  const columns = Object.keys(data[0]);
  const shown = data.slice(0, MAX_TABLE_ROWS);
  const hiddenCount = (rowCount ?? data.length) - shown.length;

  return (
    <Box sx={{ mt: 1.25 }}>
      <Box
        sx={{
          overflowX: "auto",
          border: "1px solid var(--border-color)",
          borderRadius: "10px",
        }}
      >
        <table style={{ borderCollapse: "collapse", width: "100%", fontSize: "12.5px" }}>
          <thead>
            <tr>
              {columns.map((col) => (
                <th
                  key={col}
                  style={{
                    textAlign: "left",
                    padding: "6px 10px",
                    backgroundColor: "var(--header-bg-color)",
                    color: "var(--text-color)",
                    whiteSpace: "nowrap",
                    borderBottom: "1px solid var(--border-color)",
                  }}
                >
                  {humanizeKey(col)}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {shown.map((row, i) => (
              <tr key={i} style={{ backgroundColor: i % 2 === 0 ? "var(--cell-bg-color)" : "var(--header-bg-color)" }}>
                {columns.map((col) => (
                  <td
                    key={col}
                    style={{
                      padding: "6px 10px",
                      color: "var(--text-color)",
                      whiteSpace: "nowrap",
                      borderBottom: "1px solid var(--border-color)",
                    }}
                  >
                    {formatCellValue(row[col])}
                  </td>
                ))}
              </tr>
            ))}
          </tbody>
        </table>
      </Box>
      {hiddenCount > 0 && (
        <Typography sx={{ fontSize: "11px", color: "var(--placeholder-color)", mt: 0.5 }}>
          +{hiddenCount} more row{hiddenCount === 1 ? "" : "s"}
        </Typography>
      )}
    </Box>
  );
};

const ChatbotData = ({ data, rowCount }) => {
  const mode = getDataDisplayMode(data);
  if (!mode) return null;
  if (mode === "chips") return <DataChips data={data} />;
  if (mode === "keyValue") return <DataKeyValue data={data} />;
  return <DataTable data={data} rowCount={rowCount} />;
};

const ConfirmationOptions = ({ groups, resolved, onSelect }) => {
  if (!groups || groups.length === 0) return null;

  return (
    <Stack spacing={1.25} sx={{ mt: 1.25 }}>
      {groups.map((group, gi) => (
        <Box key={gi}>
          {group.options.length > 0 && (
            <Stack direction="row" flexWrap="wrap" gap={0.75} sx={{ mt: 0.5 }}>
              {group.options.map((opt) => (
                <Button
                  key={opt.id}
                  size="small"
                  variant="outlined"
                  disabled={resolved}
                  onClick={() => onSelect(group.type, opt)}
                  sx={{
                    textTransform: "none",
                    borderRadius: "20px",
                    fontSize: "12px",
                    borderColor: "var(--add-more-btn-border)",
                    color: CHATBOT_TEAL,
                    "&:hover": { backgroundColor: "var(--add-more-btn-hover-bg)" },
                  }}
                >
                  {opt.name}
                </Button>
              ))}
            </Stack>
          )}
        </Box>
      ))}
    </Stack>
  );
};

const ChatMessage = ({ message, onSelectConfirmation }) => {
  const isUser = message.role === "user";
  const isError = message.status === "error" || message.status === "blocked" || message.status === "access_denied";

  return (
    <Box sx={{ display: "flex", justifyContent: isUser ? "flex-end" : "flex-start", mb: 1.5 }}>
      <Box
        sx={{
          ...bubbleBase,
          backgroundColor: isUser
            ? CHATBOT_TEAL
            : isError
            ? "var(--danger-bg)"
            : "var(--cell-bg-color)",
          color: isUser ? "#fff" : isError ? "var(--danger-color)" : "var(--text-color)",
          border: isUser ? "none" : "1px solid var(--border-color)",
        }}
      >
        {isError && !isUser && (
          <ErrorOutlineIcon sx={{ fontSize: 16, verticalAlign: "text-bottom", mr: 0.5 }} />
        )}
        <Typography component="span" sx={{ fontSize: "inherit", whiteSpace: "pre-wrap" }}>
          {message.text}
        </Typography>

        {!isUser && !isError && <ParamsRow params={message.params} />}
        {!isUser && !isError && (
          <ChatbotData data={message.data} rowCount={message.rowCount} />
        )}
        {!isUser && (
          <ConfirmationOptions
            groups={message.confirmationOptions}
            resolved={message.confirmationResolved}
            onSelect={(entityType, opt) =>
              onSelectConfirmation(message.id, message.sessionId, entityType, opt)
            }
          />
        )}

        {message.timestamp && (
          <Typography
            component="div"
            sx={{
              fontSize: "10px",
              mt: 0.5,
              textAlign: "right",
              color: isUser ? "rgba(255,255,255,0.7)" : "var(--placeholder-color)",
            }}
          >
            {formatMessageTimestamp(message.timestamp)}
          </Typography>
        )}
      </Box>
    </Box>
  );
};

export default ChatMessage;
